import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import ProductConsultationCTA from 'Apps/Products/Blocks/ProductConsultationCTA';

import { BottomModal } from 'Components/BottomModal';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const useStyles = makeStyles((_theme, props) => ({
  root: {
    '--backgroundPosition': props.backgroundPosition,
    display: 'grid',
    alignItems: 'center',
    width: '100%',
    backgroundImage: `url(${props.content.imageDesktop.src})`,
    backgroundSize: 'cover',
    backgroundPosition: 'var(--backgroundPosition)',
    padding: `0 ${legacyTheme.props.minimumBlockPadding}`,
    textAlign: 'center',
    [legacyTheme.breakpoints.match('md')]: {
      backgroundImage: `url(${(props.content.imageTablet || props.content.imageDesktop).src})`,
    },
    [legacyTheme.breakpoints.match('sm')]: {
      backgroundImage: `url(${props.content.imageMobile.src})`,
      display: 'block',
    },
  },

  bottomTextSection: {
    gridGap: `${legacyTheme.spacing.s144}`,
    [legacyTheme.breakpoints.match('sm')]: {
      margin: 0,
      display: 'flex',
      alignItems: 'flex-end',
      paddingLeft: `${legacyTheme.props.minimumBlockPadding}`,
    },
  },

  twoColumns: {
    gridTemplateColumns: '50% 50%',
  },
  threeColumns: {
    gridTemplateColumns: '1fr 2fr 1fr',
  },
  content: {
    // on larger screens content will be on half screen to the right
    // on small screens display will be block
    gridColumnStart: `calc(${props.columnOffset} + 1)`,
    margin: `${legacyTheme.props.verticalSpacingDes} auto`,
    maxWidth: legacyTheme.width.normal,
    [legacyTheme.breakpoints.match('md')]: {
      margin: `${legacyTheme.props.verticalSpacingMob} auto`,
    },
  },
  bottomTextContent: {
    textAlign: 'left',
    margin: 0,
    paddingBottom: `${legacyTheme.spacing.s16}`,
  },
  minHeight: {
    minHeight: 600,
    [legacyTheme.breakpoints.match('md')]: {
      minHeight: 450,
    },
  },
  fixMinHeight: {
    minHeight: 600,
  },
}));

const StyledHeader = styled(Typography)`
  margin-bottom: 0 !important;
`;
const modalContent = {
  title: (
    <>
      Get <b>35% off</b> Accessories for a limited time
    </>
  ),
  description:
    '*Save 35% on accessories. Accessories include hair towels, candles and brushes. 35% off is valid through December 10th, 2023, at 11:59 PM EST, when your order is placed on or before 12/10/2023 at 11:59 PM EST. Not valid for digital gift cards. Cannot be applied to previous orders, cannot be combined with other discounts. Valid for one use only on Accessories. Cart Restrictions Apply.',
};

const StyledProductConsultationCTA = styled(ProductConsultationCTA)`
  background-color: ${({ colorButton }) => colorButton};
  border-color: ${({ colorButton }) => colorButton};
`;

const HeroWithBackground = ({
  children,
  variant,
  hasMinHeight,
  fixMinHeight,
  className,
  height,
  bottomText,
  colorButton,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // style override through classes prop
  const { classes, cx } = useStyles(props, props?.classes ? { props } : undefined);
  const { isMobile } = useResponsiveVariant();
  const { color, headline } = props.content;

  return (
    <section
      className={cx(classes.root, classes[variant], {
        [classes.minHeight]: hasMinHeight,
        [classes.fixMinHeight]: fixMinHeight,
        [classes.bottomTextSection]: bottomText,
      })}
      data-testid="about-us-hero"
      style={{ height }}
    >
      <BottomModal
        content={modalContent}
        dataFrom="see-terms-and-conditions"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        testPrefix="accessories-promotional"
      />
      <div
        className={classNames(classes.content, className, {
          [classes.bottomTextContent]: bottomText,
        })}
      >
        {headline && (
          <StyledHeader color={color} paragraph variant="h1">
            {headline}
          </StyledHeader>
        )}
        <Spacer size={isMobile ? 12 : 24} />
        {props.content.consultationCta && (
          <StyledProductConsultationCTA
            colorButton={colorButton}
            content={props.content.consultationCta}
          />
        )}
        {children}
      </div>
    </section>
  );
};

HeroWithBackground.propTypes = {
  content: PropTypes.shape({
    color: PropTypes.oneOf(['noir', 'white']),
    consultationCta: PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
    headline: PropTypes.node,
    imageDesktop: srcOrNextImageProp.isRequired,
    imageMobile: srcOrNextImageProp.isRequired,
    imageTablet: srcOrNextImageProp,
  }),
  /** For style overriding purposes */
  classes: PropTypes.objectOf(PropTypes.string),
  children: PropTypes.node,
  fixMinHeight: PropTypes.bool,
  variant: PropTypes.string,
  columnOffset: PropTypes.number,
  backgroundPosition: PropTypes.string,
  hasMinHeight: PropTypes.bool,
  className: PropTypes.string,
  height: PropTypes.string,
  bottomText: PropTypes.bool,
  colorButton: PropTypes.string,
};

HeroWithBackground.defaultProps = {
  backgroundPosition: 'left bottom',
  classes: null,
  columnOffset: 1,
  content: {
    color: 'noir',
    headline: null,
  },
  hasMinHeight: true,
  fixMinHeight: false,
  variant: 'twoColumns',
  className: null,
  height: null,
  bottomText: false,
  colorButton: undefined,
};

export default HeroWithBackground;
